import { useState, useEffect, useRef, useMemo } from 'react'
import { Search, Plus, Trash2, Loader2, UserPlus, AlertCircle } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@components/ui/select'
import { Button } from '@components/ui/button'
import { Input } from '@components/ui/input'
import { Label } from '@components/ui/label'
import { Textarea } from '@components/ui/textarea'
import { useToast } from '@hooks/use-toast'
import { usePatients, useCreatePatient } from '@hooks/usePatients'
import { billingApi, InvoiceCreateRequest } from '@services/billing.service'
import { Patient, PatientCreateRequest } from '@/types'
import { formatCurrency } from '@/lib/utils'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess?: () => void
}

type LineItem = { service_code: string; service_description: string; quantity: number; unit_price: number }
type DiscountType = 'percentage' | 'fixed'

const emptyLineItem = (): LineItem => ({ service_code: '', service_description: '', quantity: 1, unit_price: 0 })

const today = () => new Date().toISOString().split('T')[0]
const inThirtyDays = () => new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]

const PAYMENT_METHODS: { value: string; label: string }[] = [
  { value: 'cash', label: 'Efectivo' },
  { value: 'credit_card', label: 'Tarjeta de crédito' },
  { value: 'debit_card', label: 'Tarjeta de débito' },
  { value: 'bank_transfer', label: 'Transferencia' },
  { value: 'insurance', label: 'Seguro' },
  { value: 'check', label: 'Cheque' },
]

const GENDERS: { value: string; label: string }[] = [
  { value: 'female', label: 'Femenino' },
  { value: 'male', label: 'Masculino' },
  { value: 'non_binary', label: 'No binario' },
  { value: 'prefer_not_to_say', label: 'Prefiere no decir' },
  { value: 'other', label: 'Otro' },
]

const emptyNewPatient = () => ({ first_name: '', last_name: '', date_of_birth: '', gender: 'female', phone_primary: '' })

export function CreateInvoiceDialog({ open, onOpenChange, onSuccess }: Props) {
  const { toast } = useToast()
  const createPatient = useCreatePatient()

  // Patient selection (global page has no active patient, so it must be searched)
  const [patientSearch, setPatientSearch] = useState('')
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null)
  const [showDropdown, setShowDropdown] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Inline "add new patient"
  const [showNewPatient, setShowNewPatient] = useState(false)
  const [newPatient, setNewPatient] = useState(emptyNewPatient())
  const [creatingPatient, setCreatingPatient] = useState(false)

  const { data: patientsData } = usePatients(
    patientSearch.length >= 2 ? { query: patientSearch, limit: 5 } : undefined
  )
  const searchResults = patientSearch.length >= 2 ? (patientsData?.items || []) : []

  // Invoice form
  const [invoiceDate, setInvoiceDate] = useState(today())
  const [dueDate, setDueDate] = useState(inThirtyDays())
  const [notes, setNotes] = useState('')
  const [lineItems, setLineItems] = useState<LineItem[]>([emptyLineItem()])
  const [taxRate, setTaxRate] = useState(16)
  const [discountType, setDiscountType] = useState<DiscountType>('fixed')
  const [discountValue, setDiscountValue] = useState(0)
  const [paymentMethod, setPaymentMethod] = useState<string>('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submittingStatus, setSubmittingStatus] = useState<'draft' | 'sent' | null>(null)

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  const reset = () => {
    setPatientSearch('')
    setSelectedPatient(null)
    setShowDropdown(false)
    setShowNewPatient(false)
    setNewPatient(emptyNewPatient())
    setInvoiceDate(today())
    setDueDate(inThirtyDays())
    setNotes('')
    setLineItems([emptyLineItem()])
    setTaxRate(16)
    setDiscountType('fixed')
    setDiscountValue(0)
    setPaymentMethod('')
  }

  const addLineItem = () => setLineItems((prev) => [...prev, emptyLineItem()])
  const removeLineItem = (index: number) => setLineItems((prev) => prev.filter((_, i) => i !== index))
  const updateLineItem = (index: number, field: keyof LineItem, value: string | number) => {
    setLineItems((prev) => {
      const next = [...prev]
      next[index] = { ...next[index], [field]: value }
      return next
    })
  }

  // Totals — mirrors the backend calculation (discount on subtotal, tax on the discounted base).
  const { subtotal, discountAmount, taxAmount, total } = useMemo(() => {
    const subtotal = lineItems.reduce((acc, item) => acc + item.quantity * item.unit_price, 0)
    let discountAmount = discountType === 'percentage' ? (subtotal * discountValue) / 100 : discountValue
    discountAmount = Math.max(0, Math.min(discountAmount, subtotal))
    const taxAmount = ((subtotal - discountAmount) * taxRate) / 100
    const total = subtotal - discountAmount + taxAmount
    return { subtotal, discountAmount, taxAmount, total }
  }, [lineItems, discountType, discountValue, taxRate])

  // Real-time validation.
  const validItems = lineItems.filter((i) => i.service_description.trim() && i.unit_price > 0 && i.quantity >= 1)
  const datesValid = !!invoiceDate && !!dueDate && dueDate >= invoiceDate
  const errors: string[] = []
  if (!selectedPatient) errors.push('Selecciona un paciente.')
  if (validItems.length === 0) errors.push('Agrega al menos una línea con descripción y precio.')
  if (!datesValid) errors.push('La fecha de vencimiento no puede ser anterior a la de factura.')
  const isValid = errors.length === 0

  const handleCreatePatient = async () => {
    if (!newPatient.first_name.trim() || !newPatient.last_name.trim() || !newPatient.date_of_birth) {
      toast({ title: 'Campos requeridos', description: 'Nombre, apellido y fecha de nacimiento son obligatorios.', variant: 'destructive' })
      return
    }
    setCreatingPatient(true)
    try {
      const payload: PatientCreateRequest = {
        first_name: newPatient.first_name.trim(),
        last_name: newPatient.last_name.trim(),
        date_of_birth: newPatient.date_of_birth,
        gender: newPatient.gender,
        phone_primary: newPatient.phone_primary.trim() || undefined,
      }
      const created = await createPatient.mutateAsync(payload)
      setSelectedPatient(created)
      setPatientSearch(`${created.first_name} ${created.last_name}`)
      setShowNewPatient(false)
      setNewPatient(emptyNewPatient())
      toast({ title: 'Paciente creado', description: 'El paciente se agregó y quedó seleccionado.' })
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err?.response?.data?.detail || 'No se pudo crear el paciente',
        variant: 'destructive',
      })
    } finally {
      setCreatingPatient(false)
    }
  }

  const submit = async (status: 'draft' | 'sent') => {
    if (!isValid) {
      toast({ title: 'Revisa el formulario', description: errors[0], variant: 'destructive' })
      return
    }
    setIsSubmitting(true)
    setSubmittingStatus(status)
    try {
      const payload: InvoiceCreateRequest = {
        patient_id: selectedPatient!.id,
        appointment_id: null,
        invoice_date: invoiceDate,
        due_date: dueDate,
        notes: notes.trim() || undefined,
        line_items: validItems,
        tax_rate: taxRate,
        discount_type: discountType,
        discount_value: discountValue,
        payment_method: paymentMethod || null,
        status,
      }
      await billingApi.createInvoice(payload as any)
      toast({
        title: status === 'draft' ? 'Borrador guardado' : 'Factura emitida',
        description: status === 'draft'
          ? 'La factura se guardó como borrador.'
          : 'La factura se emitió correctamente.',
      })
      onSuccess?.()
      reset()
      onOpenChange(false)
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err?.response?.data?.detail || 'No se pudo crear la factura',
        variant: 'destructive',
      })
    } finally {
      setIsSubmitting(false)
      setSubmittingStatus(null)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v) }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Crear Factura</DialogTitle>
          <DialogDescription>Crea una nueva factura para un paciente.</DialogDescription>
        </DialogHeader>
        <form onSubmit={(e) => { e.preventDefault(); submit('sent') }} className="space-y-4 py-2">
          {/* Patient picker */}
          <div className="space-y-2">
            <Label>Paciente *</Label>
            {selectedPatient ? (
              <div className="flex items-center justify-between p-3 rounded-lg border bg-ml-secondary-container/40">
                <div>
                  <p className="font-medium text-ml-primary">
                    {selectedPatient.first_name} {selectedPatient.last_name}
                  </p>
                  {selectedPatient.phone_primary && (
                    <p className="text-sm text-ml-on-surface-variant">{selectedPatient.phone_primary}</p>
                  )}
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => { setSelectedPatient(null); setPatientSearch('') }}
                >
                  Cambiar
                </Button>
              </div>
            ) : showNewPatient ? (
              <div className="space-y-3 p-3 rounded-lg border bg-ml-surface-container-low">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Nombre *</Label>
                    <Input value={newPatient.first_name} onChange={(e) => setNewPatient((p) => ({ ...p, first_name: e.target.value }))} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Apellido *</Label>
                    <Input value={newPatient.last_name} onChange={(e) => setNewPatient((p) => ({ ...p, last_name: e.target.value }))} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Fecha de nacimiento *</Label>
                    <Input type="date" max={today()} value={newPatient.date_of_birth} onChange={(e) => setNewPatient((p) => ({ ...p, date_of_birth: e.target.value }))} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Sexo</Label>
                    <Select value={newPatient.gender} onValueChange={(v) => setNewPatient((p) => ({ ...p, gender: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {GENDERS.map((g) => <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1 col-span-2">
                    <Label className="text-xs">Teléfono</Label>
                    <Input value={newPatient.phone_primary} onChange={(e) => setNewPatient((p) => ({ ...p, phone_primary: e.target.value }))} />
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" size="sm" onClick={() => { setShowNewPatient(false); setNewPatient(emptyNewPatient()) }}>
                    Cancelar
                  </Button>
                  <Button type="button" size="sm" className="bg-ml-primary text-white hover:bg-ml-primary-container" onClick={handleCreatePatient} disabled={creatingPatient}>
                    {creatingPatient ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Crear y seleccionar'}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="relative" ref={dropdownRef}>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ml-on-surface-variant/60" />
                  <Input
                    placeholder="Buscar paciente por nombre..."
                    className="pl-9"
                    value={patientSearch}
                    onChange={(e) => { setPatientSearch(e.target.value); setShowDropdown(true) }}
                    onFocus={() => setShowDropdown(true)}
                  />
                </div>
                {showDropdown && patientSearch.length >= 2 && (
                  <div className="absolute z-50 w-full mt-1 bg-card border rounded-lg shadow-lg max-h-64 overflow-y-auto">
                    {searchResults.map((patient) => (
                      <button
                        key={patient.id}
                        type="button"
                        className="w-full flex items-center gap-3 p-3 hover:bg-muted/50 transition-colors text-left"
                        onClick={() => {
                          setSelectedPatient(patient)
                          setPatientSearch(`${patient.first_name} ${patient.last_name}`)
                          setShowDropdown(false)
                        }}
                      >
                        <div className="h-8 w-8 rounded-full bg-ml-secondary-container flex items-center justify-center text-ml-on-secondary-container font-semibold text-xs shrink-0">
                          {patient.first_name[0]}{patient.last_name[0]}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">
                            {patient.first_name} {patient.last_name}
                          </p>
                          {patient.phone_primary && (
                            <p className="text-xs text-ml-on-surface-variant">{patient.phone_primary}</p>
                          )}
                        </div>
                      </button>
                    ))}
                    {searchResults.length === 0 && (
                      <p className="p-3 text-sm text-ml-on-surface-variant text-center">
                        No se encontraron pacientes
                      </p>
                    )}
                  </div>
                )}
                <button
                  type="button"
                  className="mt-2 flex items-center gap-1.5 text-xs font-bold text-ml-primary hover:underline"
                  onClick={() => { setShowDropdown(false); setShowNewPatient(true) }}
                >
                  <UserPlus className="h-3.5 w-3.5" /> Agregar nuevo paciente
                </button>
              </div>
            )}
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="invoice_date">Fecha de Factura *</Label>
              <Input
                id="invoice_date"
                type="date"
                value={invoiceDate}
                onChange={(e) => setInvoiceDate(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="due_date">Fecha de Vencimiento *</Label>
              <Input
                id="due_date"
                type="date"
                min={invoiceDate}
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                required
              />
            </div>
          </div>

          {/* Line items */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Líneas de Detalle</Label>
              <Button type="button" variant="outline" size="sm" onClick={addLineItem}>
                <Plus className="h-4 w-4 mr-1" /> Agregar Elemento
              </Button>
            </div>
            <div className="space-y-2">
              {lineItems.map((item, index) => {
                const lineTotal = item.quantity * item.unit_price
                return (
                  <div key={index} className="grid grid-cols-12 gap-2 items-end p-3 border rounded-lg">
                    <div className="col-span-2">
                      <Label className="text-xs">Código</Label>
                      <Input
                        value={item.service_code}
                        onChange={(e) => updateLineItem(index, 'service_code', e.target.value)}
                        placeholder="CODE"
                      />
                    </div>
                    <div className="col-span-4">
                      <Label className="text-xs">Descripción</Label>
                      <Input
                        value={item.service_description}
                        onChange={(e) => updateLineItem(index, 'service_description', e.target.value)}
                        placeholder="Descripción del servicio"
                      />
                    </div>
                    <div className="col-span-1">
                      <Label className="text-xs">Cant.</Label>
                      <Input
                        type="number"
                        min={1}
                        value={item.quantity}
                        onChange={(e) => updateLineItem(index, 'quantity', parseInt(e.target.value) || 1)}
                      />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs">Precio</Label>
                      <Input
                        type="number"
                        min={0}
                        step="0.01"
                        value={item.unit_price}
                        onChange={(e) => updateLineItem(index, 'unit_price', parseFloat(e.target.value) || 0)}
                      />
                    </div>
                    <div className="col-span-2 pb-2 text-right text-sm font-semibold text-ml-primary">
                      {formatCurrency(lineTotal)}
                    </div>
                    <div className="col-span-1">
                      {lineItems.length > 1 && (
                        <Button type="button" variant="ghost" size="sm" onClick={() => removeLineItem(index)}>
                          <Trash2 className="h-4 w-4 text-ml-error" />
                        </Button>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Discount, tax & payment method */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Descuento</Label>
              <div className="flex gap-2">
                <Select value={discountType} onValueChange={(v) => setDiscountType(v as DiscountType)}>
                  <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fixed">Monto</SelectItem>
                    <SelectItem value="percentage">%</SelectItem>
                  </SelectContent>
                </Select>
                <Input
                  type="number"
                  min={0}
                  step="0.01"
                  value={discountValue}
                  onChange={(e) => setDiscountValue(Math.max(0, parseFloat(e.target.value) || 0))}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="tax_rate">Impuesto / IVA (%)</Label>
              <Input
                id="tax_rate"
                type="number"
                min={0}
                max={100}
                step="0.01"
                value={taxRate}
                onChange={(e) => setTaxRate(Math.min(100, Math.max(0, parseFloat(e.target.value) || 0)))}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Método de pago</Label>
            <Select value={paymentMethod} onValueChange={setPaymentMethod}>
              <SelectTrigger><SelectValue placeholder="Selecciona un método (opcional)" /></SelectTrigger>
              <SelectContent>
                {PAYMENT_METHODS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {/* Totals breakdown */}
          <div className="rounded-lg border bg-ml-surface-container-low p-4 space-y-1.5 text-sm">
            <div className="flex justify-between text-ml-on-surface-variant">
              <span>Subtotal</span><span>{formatCurrency(subtotal)}</span>
            </div>
            {discountAmount > 0 && (
              <div className="flex justify-between text-ml-error">
                <span>Descuento{discountType === 'percentage' ? ` (${discountValue}%)` : ''}</span>
                <span>−{formatCurrency(discountAmount)}</span>
              </div>
            )}
            <div className="flex justify-between text-ml-on-surface-variant">
              <span>Impuesto ({taxRate}%)</span><span>{formatCurrency(taxAmount)}</span>
            </div>
            <div className="flex justify-between pt-1.5 border-t border-ml-outline-variant/20 font-bold text-ml-primary text-base">
              <span>Total</span><span>{formatCurrency(total)}</span>
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">Notas</Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Notas adicionales"
              rows={2}
            />
          </div>

          {/* Validation summary */}
          {!isValid && (
            <div className="flex items-start gap-2 text-xs text-ml-error bg-ml-error-container/40 rounded-lg p-2.5">
              <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
              <ul className="space-y-0.5">
                {errors.map((e) => <li key={e}>{e}</li>)}
              </ul>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => { reset(); onOpenChange(false) }}>
              Cancelar
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => submit('draft')}
              disabled={isSubmitting || !isValid}
            >
              {isSubmitting && submittingStatus === 'draft' ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Guardando...</>
              ) : 'Guardar borrador'}
            </Button>
            <Button
              type="submit"
              className="bg-ml-primary text-white hover:bg-ml-primary-container"
              disabled={isSubmitting || !isValid}
            >
              {isSubmitting && submittingStatus === 'sent' ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Emitiendo...</>
              ) : 'Emitir factura'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
